/**
 * Initials (docs/spec/07 §2): a score made the table. Three letters arcade style - up/down cycle,
 * left/right move, fire confirms, or just type - then on to the next player's score or the table.
 */

import { rankFor } from '../../game/hiscores';
import type { Screen } from '../../render/screen';
import type { KeyInput, MenuAction, PendingScore, PointerInput, SceneHost } from '../scene';
import { UiScene } from '../kit/base';
import { drawButton } from '../kit/chrome';
import type { EditorLook } from '../kit/initialsEditor';
import { drawEditor, editorPointer, rememberedInitials, rememberInitials } from '../kit/initialsEditor';
import type { InitialsState, InitialsStep } from '../kit/initialsModel';
import { initialsAction, initialsKey, startInitials } from '../kit/initialsModel';
import type { Rect } from '../kit/menuModel';
import { inRect } from '../kit/menuModel';
import { commas, isoDate } from '../kit/text';
import { ACCENT, cycle, INK, MUTED, RAINBOW } from '../kit/theme';

const TABLE_NAMES: Record<PendingScore['table'], string> = {
  classic: 'CLASSIC',
  deep: 'THE DEEP',
  expedition: 'EXPEDITION',
};
const EDITOR: EditorLook = { cx: 160, y: 112, scale: 3 };
const DONE: Rect = { x: 120, y: 160, w: 80, h: 16 };

export class InitialsScene extends UiScene {
  protected readonly heading = 'NEW HIGH SCORE!';
  protected override readonly track = 'hiscore' as const;
  protected override readonly showBack = false;
  private s: InitialsState = startInitials(rememberedInitials());
  private checked = false;
  private doneHot = false;

  constructor(host: SceneHost, private readonly entry: PendingScore) {
    super(host);
  }

  protected override describe(): string {
    const who = this.entry.who ? `${this.entry.who}, ` : '';
    return `New high score. ${who}${this.entry.score} points. Enter your initials: ${this.s.letters}`;
  }

  protected override hint(): string {
    return `▲▼ LETTER   ◄► MOVE   ${this.words().ok} NEXT`;
  }

  protected override tick(): void {
    // A later player's score may have been pushed off the table by an earlier one.
    if (this.checked) return;
    this.checked = true;
    if (!this.services.scores.qualifies(this.entry.table, this.entry.score)) this.next(-1);
  }

  protected paint(screen: Screen): void {
    const e = this.entry;
    if (e.who) screen.text(e.who, 160, 26, ACCENT, 'center');
    screen.text('YOUR SCORE', 160, 38, MUTED, 'center');
    const text = commas(e.score);
    [...text].forEach((ch, i) => {
      const x = 160 - text.length * 8 + i * 16;
      screen.text(ch, x + 1, 50, 0, 'left', 2);
      screen.text(ch, x, 49, cycle(RAINBOW, this.ms, 90, -i), 'left', 2);
    });
    const rank = rankFor(this.services.scores.table(e.table), e.score);
    const where = `${TABLE_NAMES[e.table]}  ·  ${e.reached}${e.relaxed ? '  ·  RELAXED' : ''}`;
    screen.text(where, 160, 72, INK, 'center');
    if (rank >= 0) screen.text(`THAT'S NUMBER ${rank + 1} ON THE TABLE`, 160, 83, MUTED, 'center');
    drawEditor(screen, this.s, EDITOR, this.ms);
    drawButton(screen, DONE, 'DONE', this.doneHot ? 'selected' : 'idle', this.ms);
  }

  private apply(step: InitialsStep): void {
    this.s = step.state;
    if (step.sound) this.sound(step.sound);
    if (step.done) this.submit();
    else if (step.sound === 'type' || step.sound === 'move') this.say(`${this.s.letters[this.s.cursor] ?? ''}`);
  }

  protected onAction(a: MenuAction): void {
    this.apply(initialsAction(this.s, a));
  }

  protected override back(): void {
    this.apply(initialsAction(this.s, 'back'));
  }

  keydown(k: KeyInput): boolean {
    const step = initialsKey(this.s, k.code, k.key);
    if (!step) return false;
    this.apply(step);
    return true;
  }

  protected override onPointer(p: PointerInput): void {
    this.doneHot = inRect(DONE, p.x, p.y);
    if (p.kind === 'down' && p.button !== 0) return;
    if (p.kind === 'down' && this.doneHot) return this.submit();
    const step = editorPointer(this.s, EDITOR, p.kind, p.x, p.y, p.delta);
    if (step) this.apply(step);
  }

  private submit(): void {
    const e = this.entry;
    const { year, month, day } = this.services.today();
    rememberInitials(this.s.letters);
    const rank = this.services.scores.submit(e.table, {
      initials: this.s.letters,
      score: e.score,
      reached: e.reached,
      date: isoDate(year, month, day),
      relaxed: e.relaxed,
    }, e.proof);
    this.sound('select');
    this.next(rank);
  }

  /** The next player's score that still makes its table, else the table with this one flashing. */
  private next(rank: number): void {
    const queue = [...this.entry.then];
    let n = queue.shift();
    while (n && !this.services.scores.qualifies(n.table, n.score)) n = queue.shift();
    if (n) this.host.go({ to: 'initials', entry: { ...n, then: queue } });
    else if (rank >= 0) this.host.go({ to: 'hiscores', table: this.entry.table, highlight: rank });
    else this.host.go({ to: 'hiscores', table: this.entry.table });
  }
}
